import React, { useEffect, useState } from 'react';
import './App.scss';
import { Main } from './pages/Main/Main';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ToastContainer } from 'react-toastify';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { log } from './services/LogService';
import SystemService from './services/SystemService';
import SizingToolService from './services/SizingToolService';
import { copyMetadataToLinkedPart } from './utils/Utils';

const theme = createTheme({
  palette: {
    primary: { main: '#ffb91d' },
    secondary: { main: '#000000' },
    error: { main: '#ff0000' },
  },
});

function App() {
  const [connection, setConnection] = useState<HubConnection | null>(null);

  useEffect(() => {
    const newConnection = new HubConnectionBuilder()
      .withUrl(process.env.REACT_APP_SIGNALR_HUB || '')
      .withAutomaticReconnect()
      .configureLogging(process.env.NODE_ENV === 'development' ? LogLevel.Information : LogLevel.Critical)
      .build();
    setConnection(newConnection);
  }, []);

  useEffect(() => {
    if (!connection) return;

    connection
      .start()
      .then(() => {
        log('SignalR connected.');
      })
      .catch((e) => log('SignalR connection failed: ', e));

    connection.on('RefreshSystem', async (systemId: string, workflowId: string, closeTabs: boolean) => {
      log(`Got RefreshSystem message: systemId ${systemId}, workflowId ${workflowId}, closeTabs ${closeTabs}`);
      if (!SystemService.checkSystemMatch(systemId)) return;
      const refreshed = await SystemService.refreshSystemById(systemId);
      if (refreshed && closeTabs) {
        SizingToolService.closeConfigureTab();
        SizingToolService.closeTab(workflowId);
      }
    });

    connection.on('RefreshPart', async (systemId: string, partId: string, workflowId: string, closeTabs: boolean) => {
      log(`Got RefreshPart message: systemId ${systemId}, partId ${partId}, workflowId ${workflowId}, closeTabs ${closeTabs}`);
      if (!SystemService.checkSystemMatch(systemId)) return;
      const refreshed = await SystemService.refreshPartById(systemId, partId);
      if (refreshed && closeTabs) {
        SizingToolService.closeConfigureTab();
        SizingToolService.closeTab(workflowId);
        const copyInfo = copyMetadataToLinkedPart(partId);
        if (copyInfo?.targetPartId) {
          await SystemService.updatePartMetadata(copyInfo.targetPartId, copyInfo.metadataInstances);
        }
      }
    });

    connection.on('ReturnToSizing', (systemId: string, partId: string, workflowId: string) => {
      log(`Got ReturnToSizing message: systemId ${systemId}, partId ${partId}, workflowId ${workflowId}`);
      if (!SystemService.checkSystemMatch(systemId)) return;
      SystemService.refreshPartById(systemId, partId);
      if (!SizingToolService.isPartBeingSized(workflowId)) SizingToolService.returnToSizing(partId);
      SizingToolService.closeConfigureTab();
    });
  }, [connection]);

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <ErrorBoundary>
          <ToastContainer position="top-center" />
          <Main />
        </ErrorBoundary>
      </ThemeProvider>
    </div>
  );
}

export default App;
